import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet, ListPlus, Plus, Trash2, X } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { useTracker } from '@/lib/tracker';
import type { Program } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/field';
import { Alert, Badge } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { AiButton } from './Assistant';

/** Client-provided documents are Tier 3; everything else HELM holds is Tier 2. */
export const tierForType = (type: string): 2 | 3 => (type === 'CLIENT_PROVIDED' ? 3 : 2);

interface ActionProposal {
  title: string;
  ownerId: string | null;
  ownerName: string | null;
  dueDate: string | null;
  phaseId: string | null;
}
interface DecisionProposal {
  title: string;
  description: string | null;
  targetDate: string | null;
}
interface Extracted {
  runId: string;
  coverage: { parts: number; total: number };
  actionItems: ActionProposal[];
  decisions: DecisionProposal[];
}

/**
 * Action items and decisions proposed from a meeting's minutes. Each is added through the
 * meeting's own routes and then recorded against the run; nothing is created until then.
 */
export function MinutesExtract({
  program,
  meetingId,
  minutesType,
  onAdded,
}: {
  program: Program;
  meetingId: string;
  minutesType: string;
  onAdded: () => void;
}) {
  const toast = useToast();
  const tracker = useTracker(program.id);
  const members = (tracker.data?.members ?? []).filter((m) => m.role !== 'VENDOR');
  const phases = tracker.data?.phases ?? [];
  const base = `/api/programs/${program.id}/meetings/${meetingId}`;
  const [result, setResult] = useState<Extracted | null>(null);
  const [actions, setActions] = useState<Array<ActionProposal & { state?: 'added' | 'dismissed' }>>([]);
  const [decisions, setDecisions] = useState<Array<DecisionProposal & { state?: 'added' | 'dismissed' }>>([]);

  const run = useMutation({
    mutationFn: () => api.post<Extracted>(`${base}/ai/extract`),
    onSuccess: (r) => {
      setResult(r);
      setActions(r.actionItems);
      setDecisions(r.decisions);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const accept = (recordType: 'Task' | 'RaidItem', recordId: string) =>
    api.post(`/api/programs/${program.id}/ai/runs/${result!.runId}/accept`, { recordType, recordId });
  const addAction = useMutation({
    mutationFn: async (i: number) => {
      const a = actions[i]!;
      const t = await api.post<{ id: string }>(`${base}/action-items`, {
        title: a.title,
        ownerId: a.ownerId,
        dueDate: a.dueDate,
        phaseId: a.phaseId,
      });
      await accept('Task', t.id);
      return i;
    },
    onSuccess: (i) => {
      setActions((xs) => xs.map((x, j) => (j === i ? { ...x, state: 'added' } : x)));
      onAdded();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const addDecision = useMutation({
    mutationFn: async (i: number) => {
      const d = decisions[i]!;
      const item = await api.post<{ id: string; ref: string }>(`${base}/decisions`, {
        title: d.title,
        ...(d.description ? { description: d.description } : {}),
        ...(d.targetDate ? { targetDate: d.targetDate } : {}),
      });
      await accept('RaidItem', item.id);
      return { i, ref: item.ref };
    },
    onSuccess: ({ i, ref }) => {
      setDecisions((xs) => xs.map((x, j) => (j === i ? { ...x, state: 'added' } : x)));
      toast.ok(`${ref} added to the RAID log.`);
      onAdded();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const setA = (i: number, p: Partial<ActionProposal & { state: 'added' | 'dismissed' }>) =>
    setActions((xs) => xs.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const setD = (i: number, p: Partial<DecisionProposal & { state: 'added' | 'dismissed' }>) =>
    setDecisions((xs) => xs.map((x, j) => (j === i ? { ...x, ...p } : x)));

  return (
    <>
      <AiButton program={program} tier={tierForType(minutesType)} onClick={() => run.mutate()} loading={run.isPending}>
        <ListPlus /> Extract from minutes
      </AiButton>
      <Dialog
        open={!!result}
        onClose={() => setResult(null)}
        wide
        title="From the minutes"
        description="Proposals only. Check each one; adding it creates the action item or decision on this meeting with an AI-assisted badge."
        footer={
          <Button variant="secondary" onClick={() => setResult(null)}>
            Close
          </Button>
        }
      >
        {result && (
          <div className="space-y-4 text-sm">
            {result.coverage.parts < result.coverage.total && (
              <Alert tone="warn" title="Long minutes">
                Read the first {result.coverage.parts} of {result.coverage.total} parts.
              </Alert>
            )}
            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Action items</h3>
              {!actions.length && <p className="text-muted">None found.</p>}
              {actions.map((a, i) =>
                a.state ? (
                  <p key={i} className="text-muted">
                    {a.state === 'added' ? <Badge tone="ok">Added</Badge> : 'Dismissed'} {a.title}
                  </p>
                ) : (
                  <div key={i} className="grid gap-2 rounded-md border border-border p-3 md:grid-cols-2">
                    <Field label="Action" htmlFor={`ai-t-${i}`} className="md:col-span-2">
                      <Input
                        id={`ai-t-${i}`}
                        value={a.title}
                        maxLength={200}
                        onChange={(e) => setA(i, { title: e.target.value })}
                      />
                    </Field>
                    <Field
                      label="Owner"
                      htmlFor={`ai-o-${i}`}
                      hint={!a.ownerId && a.ownerName ? `Minutes say: ${a.ownerName}` : undefined}
                    >
                      <Select
                        id={`ai-o-${i}`}
                        value={a.ownerId ?? ''}
                        onChange={(e) => setA(i, { ownerId: e.target.value || null })}
                      >
                        <option value="">Choose</option>
                        {members.map((m) => (
                          <option key={m.userId} value={m.userId}>
                            {m.name}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Due" htmlFor={`ai-d-${i}`}>
                      <Input
                        id={`ai-d-${i}`}
                        type="date"
                        value={a.dueDate ?? ''}
                        onChange={(e) => setA(i, { dueDate: e.target.value || null })}
                      />
                    </Field>
                    <Field label="Phase" htmlFor={`ai-p-${i}`}>
                      <Select
                        id={`ai-p-${i}`}
                        value={a.phaseId ?? ''}
                        onChange={(e) => setA(i, { phaseId: e.target.value || null })}
                      >
                        <option value="">Choose</option>
                        {phases.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.number}. {p.name}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <div className="flex items-end gap-2">
                      <Button
                        size="sm"
                        disabled={!a.title.trim() || !a.ownerId || !a.dueDate || !a.phaseId}
                        loading={addAction.isPending && addAction.variables === i}
                        onClick={() => addAction.mutate(i)}
                      >
                        Add action item
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setA(i, { state: 'dismissed' })}>
                        <X /> Dismiss
                      </Button>
                    </div>
                  </div>
                ),
              )}
            </section>
            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Decisions</h3>
              {!decisions.length && <p className="text-muted">None found.</p>}
              {decisions.map((d, i) =>
                d.state ? (
                  <p key={i} className="text-muted">
                    {d.state === 'added' ? <Badge tone="ok">Added</Badge> : 'Dismissed'} {d.title}
                  </p>
                ) : (
                  <div key={i} className="space-y-2 rounded-md border border-border p-3">
                    <Field label="Decision needed" htmlFor={`ai-dt-${i}`}>
                      <Input
                        id={`ai-dt-${i}`}
                        value={d.title}
                        maxLength={200}
                        onChange={(e) => setD(i, { title: e.target.value })}
                      />
                    </Field>
                    <Field label="Context" htmlFor={`ai-dd-${i}`}>
                      <Textarea
                        id={`ai-dd-${i}`}
                        rows={2}
                        value={d.description ?? ''}
                        onChange={(e) => setD(i, { description: e.target.value || null })}
                      />
                    </Field>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={!d.title.trim()}
                        loading={addDecision.isPending && addDecision.variables === i}
                        onClick={() => addDecision.mutate(i)}
                      >
                        Add decision
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setD(i, { state: 'dismissed' })}>
                        <X /> Dismiss
                      </Button>
                    </div>
                  </div>
                ),
              )}
            </section>
          </div>
        )}
      </Dialog>
    </>
  );
}

const CATEGORIES = ['Key term', 'Date', 'Notice period', 'Obligation', 'Deconversion'] as const;
interface Term {
  category: (typeof CATEGORIES)[number];
  term: string;
  detail: string;
  reference: string | null;
}

/** Key terms from an agreement as an editable table, filed as a working paper on request. */
export function TermsExtract({
  program,
  documentId,
  documentTitle,
  documentType,
}: {
  program: Program;
  documentId: string;
  documentTitle: string;
  documentType: string;
}) {
  const toast = useToast();
  const nav = useNavigate();
  const qc = useQueryClient();
  const base = `/api/programs/${program.id}/documents/${documentId}/ai/terms`;
  const [result, setResult] = useState<{
    runId: string;
    summary: string;
    coverage: { parts: number; total: number };
  } | null>(null);
  const [rows, setRows] = useState<Term[]>([]);
  const [title, setTitle] = useState(`Key terms, ${documentTitle}`);
  const run = useMutation({
    mutationFn: () =>
      api.post<{ runId: string; summary: string; coverage: { parts: number; total: number }; terms: Term[] }>(base),
    onSuccess: (r) => {
      setResult(r);
      setRows(r.terms);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const file = useMutation({
    mutationFn: () =>
      api.post<{ documentId: string }>(`${base}/file`, {
        runId: result!.runId,
        title,
        rows: rows.map((r) => ({ ...r, reference: r.reference?.trim() || null })),
      }),
    onSuccess: (r) => {
      toast.ok('Filed as a working paper.');
      void qc.invalidateQueries({ queryKey: ['documents', program.id] });
      setResult(null);
      nav(`/p/${program.id}/documents/${r.documentId}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const set = (i: number, p: Partial<Term>) => setRows((xs) => xs.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const valid = rows.length > 0 && rows.every((r) => r.term.trim() && r.detail.trim()) && title.trim().length >= 3;

  return (
    <>
      <AiButton program={program} tier={tierForType(documentType)} onClick={() => run.mutate()} loading={run.isPending}>
        <FileSpreadsheet /> Extract key terms
      </AiButton>
      <Dialog
        open={!!result}
        onClose={() => setResult(null)}
        wide
        title="Key terms"
        description="Review every row against the agreement. Filing saves this table as a working paper with an AI-assisted badge."
        footer={
          <>
            <Button variant="secondary" onClick={() => setResult(null)}>
              Close
            </Button>
            <Button onClick={() => file.mutate()} loading={file.isPending} disabled={!valid}>
              File as working paper
            </Button>
          </>
        }
      >
        {result && (
          <div className="space-y-3 text-sm">
            <p>{result.summary}</p>
            {result.coverage.parts < result.coverage.total && (
              <Alert tone="warn" title="Long agreement">
                Read the first {result.coverage.parts} of {result.coverage.total} parts. Check the rest by hand.
              </Alert>
            )}
            <Field label="Working paper title" htmlFor="terms-title">
              <Input id="terms-title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
            </Field>
            {rows.map((r, i) => (
              <div
                key={i}
                className="grid gap-2 rounded-md border border-border p-3 md:grid-cols-[150px_1fr_120px_auto]"
              >
                <Select
                  aria-label={`Category ${i + 1}`}
                  value={r.category}
                  onChange={(e) => set(i, { category: e.target.value as Term['category'] })}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </Select>
                <div className="space-y-1">
                  <Input
                    aria-label={`Term ${i + 1}`}
                    value={r.term}
                    maxLength={300}
                    onChange={(e) => set(i, { term: e.target.value })}
                  />
                  <Textarea
                    aria-label={`Detail ${i + 1}`}
                    rows={2}
                    value={r.detail}
                    maxLength={3000}
                    onChange={(e) => set(i, { detail: e.target.value })}
                  />
                </div>
                <Input
                  aria-label={`Reference ${i + 1}`}
                  placeholder="Section"
                  value={r.reference ?? ''}
                  maxLength={200}
                  onChange={(e) => set(i, { reference: e.target.value })}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove row ${i + 1}`}
                  onClick={() => setRows(rows.filter((_, j) => j !== i))}
                >
                  <Trash2 />
                </Button>
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRows([...rows, { category: 'Key term', term: '', detail: '', reference: null }])}
            >
              <Plus /> Add a row
            </Button>
          </div>
        )}
      </Dialog>
    </>
  );
}
